import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from './Select';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import { bauService } from '../services/api';

// Standing "business as usual" duties — what a person's routine work IS.
// Deliberately no status or completion: one-off work that gets ticked off
// belongs in Todos, not here.

const FREQUENCIES = [
  { value: 'daily',     label: 'Daily',     color: '#0369A1', bg: '#E0F2FE' },
  { value: 'weekly',    label: 'Weekly',    color: '#7C3AED', bg: '#F5F3FF' },
  { value: 'monthly',   label: 'Monthly',   color: '#B45309', bg: '#FEF3C7' },
  { value: 'quarterly', label: 'Quarterly', color: '#9A3412', bg: '#FFEDD5' },
  { value: 'yearly',    label: 'Yearly',    color: '#15803D', bg: '#DCFCE7' },
  { value: 'as_needed', label: 'As Needed', color: '#475569', bg: '#F1F5F9' },
];
const userName = (u) => u ? [u.firstName, u.lastName].filter(Boolean).join(' ') : '—';

const emptyDraft = { title: '', description: '', frequency: 'daily' };

const BauPanel = ({ isManager, assignableUsers, filterUserId }) => {
  const { currentUser, showNotification } = useInvoice();
  const confirm = useConfirm();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);

  // "All except me" is its own mode: it lists everyone else, and adding files
  // the duty against all of them at once rather than against the viewer.
  const excludeMe = isManager && filterUserId === 'exclude_me';
  const targetUserId = isManager && filterUserId && !excludeMe
    ? filterUserId
    : (isManager ? '' : String(currentUser?.id || ''));

  // showNotification gets a new identity on every context render, so depending
  // on it directly would rebuild `load` and refire the effect below — turning
  // the context's background poll into a visible refetch. Hold it in a ref.
  const notifyRef = useRef(showNotification);
  notifyRef.current = showNotification;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await bauService.list(
        excludeMe
          ? { excludeUserId: currentUser?.id }
          : { assignedToId: targetUserId || undefined }
      ));
    } catch (err) {
      notifyRef.current(err.message || 'Failed to load BAU list', 'error');
    } finally {
      setLoading(false);
    }
  }, [targetUserId, excludeMe, currentUser?.id]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!draft.title.trim()) return;
    setSaving(true);
    try {
      const res = await bauService.create({
        ...draft,
        ...(excludeMe
          ? { assignToAllExcept: currentUser?.id }
          : (isManager && targetUserId ? { assignedToId: targetUserId } : {})),
      });
      if (excludeMe) showNotification(res.message || 'Duty added', 'success');
      setDraft(emptyDraft);
      await load();
    } catch (err) {
      showNotification(err.message || 'Failed to add duty', 'error');
    } finally {
      setSaving(false);
    }
  };

  // How many people share this duty (i.e. it came from one "add for everyone").
  // Only managers can act on the whole batch.
  const groupSize = (item) => (
    item.groupId ? items.filter(i => i.groupId === item.groupId).length : 1
  );

  const handleSaveEdit = async (item) => {
    if (!editDraft.title.trim()) return;
    let applyToGroup = false;

    const shared = isManager && item.groupId;
    if (shared) {
      const choice = await confirm(
        `“${item.title}” is part of a routine shared with other colleagues. Update it for everyone who has it, or only for ${userName(item.assignedTo)}?`,
        { title: 'Update Shared Duty', confirmLabel: 'Update for everyone', altLabel: 'Only this person' }
      );
      if (!choice) return;                 // cancelled
      applyToGroup = choice !== 'alt';     // 'alt' = only this person
    }

    try {
      const res = await bauService.update(item.id, { ...editDraft, applyToGroup });
      if (applyToGroup) showNotification(res.message || 'Updated', 'success');
      setEditingId(null);
      await load();
    } catch (err) {
      showNotification(err.message || 'Failed to update duty', 'error');
    }
  };

  const handleDelete = async (item) => {
    let applyToGroup = false;
    const shared = isManager && item.groupId;

    if (shared) {
      const choice = await confirm(
        `“${item.title}” is part of a routine shared with other colleagues. Remove it from everyone's list, or only from ${userName(item.assignedTo)}'s?`,
        { title: 'Remove Shared Duty', confirmLabel: 'Remove from everyone', altLabel: 'Only this person', danger: true }
      );
      if (!choice) return;
      applyToGroup = choice !== 'alt';
    } else {
      const ok = await confirm(`Remove "${item.title}" from the routine list?`, {
        title: 'Remove BAU Duty', confirmLabel: 'Remove', danger: true,
      });
      if (!ok) return;
    }

    try {
      const res = await bauService.remove(item.id, applyToGroup);
      if (applyToGroup) showNotification(res.message || 'Removed', 'success');
      await load();
    } catch (err) {
      showNotification(err.message || 'Failed to remove duty', 'error');
    }
  };

  // With no single person selected, a manager sees many people's lists —
  // group by person first, then by frequency within each person.
  const showPerPerson = isManager && (excludeMe || !targetUserId);
  const byPerson = {};
  items.forEach(it => {
    const key = it.assignedToId;
    if (!byPerson[key]) byPerson[key] = { user: it.assignedTo, items: [] };
    byPerson[key].items.push(it);
  });

  const renderGroupedByFrequency = (list) => (
    <>
      {FREQUENCIES.map(f => {
        const group = list.filter(i => i.frequency === f.value);
        if (group.length === 0) return null;
        return (
          <div key={f.value} style={{ marginBottom: '14px' }}>
            <div style={{
              display: 'inline-block', fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: f.color, background: f.bg,
              padding: '2px 9px', borderRadius: '3px', marginBottom: '6px',
            }}>
              {f.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {group.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '7px 10px', borderRadius: '6px', background: 'var(--color-gray-50, #F9FAFB)' }}>
                  {editingId === item.id ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <input className="form-input" autoFocus value={editDraft.title}
                        onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(item); if (e.key === 'Escape') setEditingId(null); }}
                        style={{ fontSize: '13px', padding: '4px 8px' }} />
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ width: '130px' }}>
                          <Select value={editDraft.frequency} onChange={v => setEditDraft(d => ({ ...d, frequency: v }))}
                            options={FREQUENCIES.map(f2 => ({ value: f2.value, label: f2.label }))}
                            buttonStyle={{ fontSize: '12px', padding: '3px 8px' }} />
                        </div>
                        <input className="form-input" placeholder="Note (optional)" value={editDraft.description || ''}
                          onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                          style={{ fontSize: '12px', padding: '4px 8px', flex: '1 1 160px' }} />
                        <button className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '3px 10px' }} onClick={() => handleSaveEdit(item)}>Save</button>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '3px 10px' }} onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span style={{ color: 'var(--color-gray-400)', fontSize: '13px', lineHeight: '1.5', flexShrink: 0 }}>•</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', color: 'var(--color-text)' }}>
                          {item.title}
                          {item.groupId && groupSize(item) > 1 && (
                            <span title={`Shared routine — ${groupSize(item)} people have this`}
                              style={{ marginLeft: '7px', fontSize: '10px', fontWeight: 600, color: '#475569', background: '#F1F5F9', padding: '1px 6px', borderRadius: '3px', whiteSpace: 'nowrap' }}>
                              shared
                            </span>
                          )}
                        </div>
                        {item.description && (
                          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '1px' }}>{item.description}</div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 9px' }}
                          onClick={() => { setEditingId(item.id); setEditDraft({ title: item.title, description: item.description || '', frequency: item.frequency }); }}>
                          Edit
                        </button>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 9px', color: '#DC2626' }}
                          onClick={() => handleDelete(item)}>
                          Remove
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );

  const targetName = targetUserId
    ? userName(assignableUsers.find(u => String(u.id) === String(targetUserId)) || currentUser)
    : null;

  return (
    <div>
      {/* Add row */}
      <div className="card mb-md">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="form-input"
            placeholder={
              excludeMe ? 'Add a routine duty for everyone except you…'
              : targetName ? `Add a routine duty for ${targetName}…`
              : 'Add a routine duty…'
            }
            value={draft.title}
            onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            style={{ flex: '2 1 240px', fontSize: '13px', padding: '6px 10px' }}
          />
          <div style={{ width: '140px', flexShrink: 0 }}>
            <Select value={draft.frequency} onChange={v => setDraft(d => ({ ...d, frequency: v }))}
              options={FREQUENCIES.map(f => ({ value: f.value, label: f.label }))}
              buttonStyle={{ fontSize: '13px', padding: '6px 10px' }} />
          </div>
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving || !draft.title.trim()}>
            {saving ? 'Adding…' : excludeMe ? '+ Add for everyone' : '+ Add'}
          </button>
        </div>
        {isManager && excludeMe && (
          <div style={{ fontSize: '11px', color: '#B45309', marginTop: '8px' }}>
            Adding here gives this duty to <strong>every active user except you</strong>, as a separate entry on each of their lists.
          </div>
        )}
        {isManager && !excludeMe && !targetUserId && (
          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '8px' }}>
            Adding here files the duty against you. Pick a person in the “Assigned To” filter above to add duties for someone else.
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-secondary" style={{ fontSize: '13px', padding: '20px 0' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔁</div>
          <div className="empty-state-text">No routine duties listed yet</div>
        </div>
      ) : showPerPerson ? (
        Object.values(byPerson).map(({ user, items: list }) => (
          <div key={user?.id || 'none'} className="card mb-md">
            <div className="card-header">
              {userName(user)}
              <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: '6px' }}>
                ({list.length})
              </span>
            </div>
            {renderGroupedByFrequency(list)}
          </div>
        ))
      ) : (
        <div className="card">
          <div className="card-header">
            {targetName ? `${targetName} — routine duties` : 'Routine duties'}
            <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: '6px' }}>
              ({items.length})
            </span>
          </div>
          {renderGroupedByFrequency(items)}
        </div>
      )}
    </div>
  );
};

export default BauPanel;
